import { computed, ref } from 'vue';
import { useInjectDebtorDialog } from '../../useInjectDebtorDialog';
import { useLocalI18n } from '@/hooks/useLocalI18n';
import { useFetchEstateOwners } from './useFetchEstateOwners';
import { UniqueDebtorsDocument, UpdateUniqueDebtorsDocument, useDocumentsApi } from '@/hooks/useDocumentsApi';
import { ActiveFormFieldType } from '@/hooks/useActiveForm';
import { mbToBites } from '@/hooks/useFileManager';
import { useModelDialog } from '@/hooks/useModelDialog';
import { ApiResponse, ApiResponseError } from '@/service/api';
import { ErrorsMap } from '@/hooks/useErrors';
import { RosreestrEstateObjectCharacteristic, useRosreestr } from '@/hooks/useRosreestr';
import { useProtectedDefaultCompany } from '@/hooks/useProtectedDefaultCompany';
import { isNil } from 'lodash';

export const useInvoiceModal = () => {
  const { debtorId, productionType } = useInjectDebtorDialog();
  const { companyId } = useProtectedDefaultCompany();
  const { t } = useLocalI18n('debtor.documents.forms.invoiceDocument');
  const { fetchUniqueDebtorsDocument, updateUniqueDebtorsDocument, uploadUniqueDebtorsDocument } = useDocumentsApi();
  const { fetchEstateOwners } = useFetchEstateOwners();
  const { updateEstateObjectCharacteristic } = useRosreestr();

  const key = 'invoice';

  const owners = ref<RosreestrEstateObjectCharacteristic[]>([]);

  fetchEstateOwners(debtorId)
    .then((data: RosreestrEstateObjectCharacteristic[]) => {
      owners.value = data;
    });

  const fields = computed(() => {
    const base = [
      {
        key: 'estate_object',
        field: 'estate_object',
        type: ActiveFormFieldType.searchSelect,
        options: {
          label: t('fields.estate_object'),
          state: ['drop-up', 'primary'],
          prefetch: true,
          isAutocomplete: false,
          displayField: ['owner_name', 'ownership_registration_number'],
          valueField: 'id',
          searchQuery: () => owners,
        },
      },
      {
        key: 'file',
        field: 'file',
        type: ActiveFormFieldType.file,
        options: {
          label: t('fields.file'),
          state: ['primary', 'short'],
          maxSize: mbToBites(30),
        },
      },
    ];

    if (owners.value.length) {
      base.push({
        key: 'fraction_in_ownership',
        field: 'fraction_in_ownership',
        type: ActiveFormFieldType.input,
        options: {
          label: t('fields.fraction_in_ownership'),
          state: ['primary', 'short'],
        } as any,
      });
    }

    return base;
  });

  const modelDialogCreate = useModelDialog<
    UpdateUniqueDebtorsDocument,
    UniqueDebtorsDocument
  >({
    key: 'create_invoice',
    title: 'Добавление счета-фактуры',
    // @ts-ignore
    fields,
    onModelUpdate: async (payload) => {
      return uploadPipeline(payload);
    },
  });

  const modelDialogUpdate = useModelDialog<
    UpdateUniqueDebtorsDocument,
    UniqueDebtorsDocument,
    number
  >({
    key: 'edit_invoice',
    title: 'Редактирование счета-фактуры',
    // @ts-ignore
    fields,
    getForm: (id) => fetchUniqueDebtorsDocument({
      filters: { id },
    }, key) as unknown as Promise<ApiResponse<UpdateUniqueDebtorsDocument>>,
    onModelUpdate: async (payload, id) => {
      return uploadPipeline(payload, id);
    },
  });

  function hasErrors(payload: UpdateUniqueDebtorsDocument) {
    const { errorsMap, hasErrors } = Object.entries(payload).reduce(
      (acc, [key, value]) => {
        if (['file'].includes(key)) {
          if (!value) {
            acc.errorsMap[key] = [`Поле ${t(`fields.${key}`)} обязательно`];
            acc.hasErrors = true;
          }
        }

        return acc;
      }, {
        errorsMap: {},
        hasErrors: false,
      } as { errorsMap: ErrorsMap<any>; hasErrors: boolean},
    );

    return hasErrors ? errorsMap : null;
  }

  function createInvoice(payload: UpdateUniqueDebtorsDocument) {
    return uploadUniqueDebtorsDocument({
      ...payload,
      key,
      debtor: debtorId.value,
      production_type: productionType.value,
      company: companyId.value,
    });
  }

  function updateInvoice(payload: UpdateUniqueDebtorsDocument) {
    const { file, ...rest } = payload;

    return updateUniqueDebtorsDocument({
      ...rest,
      ...(typeof file === 'string' ? {} : { file }),
      key,
    } as UpdateUniqueDebtorsDocument, payload.id as number);
  }

  async function uploadPipeline(payload: UpdateUniqueDebtorsDocument, id?: number) {
    const errors = hasErrors(payload);

    if (errors) {
      return {
        status: false,
        response: errors,
      } as ApiResponseError;
    }

    const invoice = isNil(id)
      ? await createInvoice(payload)
      : await updateInvoice(payload);

    if (invoice.status && payload.estate_object && 'fraction_in_ownership' in payload) {
      const owner = owners.value.find((x) => x.id === (payload as any).estate_object);

      if (owner) {
        await updateEstateObjectCharacteristic({
          id: (payload as any).estate_object,
          model: {
            ...owner,
            fraction_in_ownership: (payload as any).fraction_in_ownership as string,
          },
        });
      }
    }

    return invoice;
  }

  return {
    openCreateDialog: modelDialogCreate.showDialog,
    openUpdateDialog: modelDialogUpdate.showDialog,
  };
};
